import { NodeRendererProps } from "react-arborist";
import CheckboxIcon from "./CheckboxIcon";
import ArrowIcon from "./ArrowIcon";

import "./AccessorTreeNode.scss";
import { Accessor, Constituent, PropertyAttribute } from "../../../types/types";

interface IBaseTreeNode {
	id: string;
	name: string;
}

export interface IAccessorTreeNode extends IBaseTreeNode {
	accessor: Accessor;
}

export interface IConstituentTreeNode extends IBaseTreeNode {
	constituent: Constituent;
	children: ConstituentTreeNode[];
}

export interface IPropertyTreeNode extends IBaseTreeNode {
	children: PropertyTreeNode[];
}

export interface IPropertyAttributeNode extends IBaseTreeNode {
	attribute: PropertyAttribute;
}

export type ConstituentTreeNode = IConstituentTreeNode | IAccessorTreeNode;
export type PropertyTreeNode = IPropertyTreeNode | IPropertyAttributeNode;
export type TreeNode = ConstituentTreeNode | PropertyTreeNode;

export type TreeNodeSelection = "all" | "none" | "some";

export const isConstituentTreeNode = (treeNode: TreeNode): treeNode is ConstituentTreeNode =>
	"constituent" in treeNode || "accessor" in treeNode;
// We only have two options so just negate the previous guard and assume it is the opposite.
export const isPropertyTreeNode = (treeNode: TreeNode): treeNode is PropertyTreeNode =>
	!isConstituentTreeNode(treeNode);

interface AccessorTreeNodeProps extends NodeRendererProps<TreeNode> {
	selection: TreeNodeSelection;
	onSelect: (node: TreeNode, selection: TreeNodeSelection) => void;
}

export default function AccessorTreeNode({ node, selection, onSelect }: AccessorTreeNodeProps) {
	const onToggleOpen = () => {
		if (node.isOpen) node.close();
		else node.open();
	};

	const onToggleSelection = () => onSelect(node.data, selection);

	const classes = ["node"];

	if (node.isLeaf) classes.push("leaf");

	return (
		<div className={classes.join(" ")}>
			{!node.isLeaf && <ArrowIcon isOpen={node.isOpen} onClick={onToggleOpen} />}
			<CheckboxIcon variant={selection} onClick={onToggleSelection} />
			<span className="name" onClick={onToggleSelection}>
				{node.data.name}
			</span>
		</div>
	);
}
